import React from "react";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import FiltrosColaboradores from "../../components/colaboradores/FiltrosColaboradores.tsx";
import TableColaboradores from "./TableColaboradores.tsx";
import PaginationColaboradores from "./PaginationColaboradores.tsx";
import { fetchPartners } from "../../services/partner.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import { can } from "../../auth/can.ts";
import {
  resetState,
  setColaboradores,
  setDataToRemove,
  setFetch,
  setLoading,
  setMessageModal,
  setModalAcountant,
  setModalCredentials,
  setModalDelete,
  setPage,
  setShow,
  setShowModal,
  setShowModalImportExcel,
  setShowModalExportExcel,
  setTotal,
} from "../../store/slices/colaboradoresSlice.ts";
import Title from "../../components/Title.tsx";
import Layout from "../../components/Layout.tsx";
import { readBranches } from "../../services/branch.service.ts";
import ModalContainer from "../../components/ModalContainer.tsx";
import ReactivatePartner from "../../components/talento-humano/ReactivatePartner.tsx";
import GetCredentials from "../../components/colaboradores/GetCredentials.tsx";
import DeletePartner from "../../components/talento-humano/DeletePartner.tsx";
import TableColaboradoresContador from "./TableColaboradoresContador.tsx";
import EditSalary from "../../components/colaboradores/EditSalary.tsx";
import ImportPartners from "../../components/colaboradores/ImportPartners.tsx";
import ExportColaboradoresModal from "../../components/colaboradores/ExportColaboradoresModal.tsx";
import EditColaboradorMessage from "./EditColaboradorMessage.tsx";
import { useParams } from "react-router-dom";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { IBranch } from "../../models/Branch";
import { setQuery } from "../../store/slices/colaboradoresSlice.ts";
import {
  loadBranchOrder,
  saveBranchOrder,
  STORAGE_KEY_BRANCH_ORDER,
} from "../../utils/branchOrderStorage.ts";

const BranchButtonsColaboradores: React.FC = () => {
  const dispatch = useDispatch();
  const { query } = useSelector((state: RootState) => state.colaboradores);
  const [branches, setBranches] = React.useState<IBranch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = React.useState<string>("all");
  const [customOrderIds, setCustomOrderIds] = React.useState<string[] | null>(
    () => loadBranchOrder()
  );
  const [isOrdering, setIsOrdering] = React.useState(false);
  const [draggedId, setDraggedId] = React.useState<string | null>(null);

  const fetchBranches = async () => {
    try {
      const branchList = await readBranches();
      setBranches(branchList || []);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    fetchBranches();
  }, []);

  React.useEffect(() => {
    if (!query) {
      setSelectedBranchId("all");
      return;
    }
    const params = new URLSearchParams(query);
    const branchParam = params.get("branch");
    setSelectedBranchId(branchParam ?? "all");
  }, [query]);

  const defaultSortedBranches = React.useMemo(
    () =>
      [...branches].sort((a, b) => {
        const aRestaurant = Boolean(a.isRestaurant);
        const bRestaurant = Boolean(b.isRestaurant);
        if (aRestaurant !== bRestaurant) return aRestaurant ? -1 : 1;
        return a.name.localeCompare(b.name, "es");
      }),
    [branches]
  );

  const sortedBranches = React.useMemo(() => {
    if (!customOrderIds || customOrderIds.length === 0) {
      return defaultSortedBranches;
    }
    const byId = new Map(branches.map((b) => [String(b.id), b]));
    const seen = new Set<string>();
    const ordered: IBranch[] = [];
    for (const id of customOrderIds) {
      const b = byId.get(id);
      if (b) {
        ordered.push(b);
        seen.add(String(b.id));
      }
    }
    defaultSortedBranches.forEach((b) => {
      if (!seen.has(String(b.id))) ordered.push(b);
    });
    return ordered.length > 0 ? ordered : defaultSortedBranches;
  }, [branches, customOrderIds, defaultSortedBranches]);

  // Paleta Chilli (chili-app/tailwind.config.js): un color por sucursal
  const CHILLI_PALETTE: { main: string; light: string }[] = [
    { main: "#FA0A0A", light: "#fecaca" },       // chilliRed
    { main: "#50B7C0", light: "#a5f3f7" },       // chilliGreen
    { main: "#F7B917", light: "#fef3c7" },       // chilliYellow
    { main: "#1E90FF", light: "#bfdbfe" },       // chilliBlue
    { main: "#800080", light: "#f3e8ff" },       // chilliPurple
    { main: "#FFA500", light: "#ffedd5" },       // chilliOrange
    { main: "#FF69B4", light: "#fce7f3" },       // chilliPink
    { main: "#A52A2A", light: "#fef2f2" },       // chilliBrown
    { main: "#00FFFF", light: "#cffafe" },       // chilliCyan
    { main: "#FF00FF", light: "#fae8ff" },      // chilliMagenta
    { main: "#00FF00", light: "#dcfce7" },      // chilliLime
    { main: "#008080", light: "#ccfbf1" },       // chilliTeal
    { main: "#4B0082", light: "#e0e7ff" },       // chilliIndigo
    { main: "#DC2C27", light: "#fee2e2" },       // chilliButtons
    { main: "#50834C", light: "#dcfce7" },       // greenButtons
  ];

  const getBranchColorByIndex = (index: number) =>
    CHILLI_PALETTE[index % CHILLI_PALETTE.length];

  const getBranchButtonStyle = (
    isSelected: boolean,
    isAll?: boolean,
    branchIndex?: number
  ): React.CSSProperties => {
    if (isAll) {
      return {
        background: isSelected
          ? "linear-gradient(135deg, #374151 0%, #1f2937 100%)"
          : "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
        color: isSelected ? "#ffffff" : "#374151",
        border: "none",
        borderRadius: "16px",
        padding: "0.5rem 1rem",
        fontSize: "0.875rem",
        fontWeight: 500,
        minWidth: "80px",
        transition: "all 0.3s ease-in-out",
        boxShadow: isSelected
          ? "0 4px 12px rgba(55, 65, 81, 0.35)"
          : "0 2px 4px rgba(0, 0, 0, 0.08)",
        transform: isSelected ? "scale(1.05)" : "scale(1)",
        whiteSpace: "nowrap",
      };
    }
    const { main, light } = getBranchColorByIndex(branchIndex ?? 0);
    return {
      background: isSelected
        ? `linear-gradient(135deg, ${main} 0%, ${main}dd 100%)`
        : `linear-gradient(135deg, ${light} 0%, ${light}ee 100%)`,
      color: isSelected ? "#ffffff" : main,
      border: isSelected ? "none" : `1px solid ${main}40`,
      borderRadius: "16px",
      padding: "0.5rem 1rem",
      fontSize: "0.875rem",
      fontWeight: 500,
      minWidth: "80px",
      transition: "all 0.3s ease-in-out",
      boxShadow: isSelected
        ? `0 4px 12px ${main}50, 0 2px 4px rgba(0,0,0,0.1)`
        : "0 2px 4px rgba(0, 0, 0, 0.08)",
      transform: isSelected ? "scale(1.05)" : "scale(1)",
      whiteSpace: "nowrap",
    };
  };

  const handleBranchClick = (branchId: string) => {
    if (isOrdering) return;
    const idAsString = String(branchId);
    const params = new URLSearchParams(query || "");
    if (idAsString === "all") {
      params.delete("branch");
    } else {
      params.set("branch", idAsString);
    }
    setSelectedBranchId(idAsString);
    dispatch(setQuery(params.toString()));
  };

  const handleDragStart = (e: React.DragEvent, branchId: string) => {
    if (!isOrdering) return;
    setDraggedId(String(branchId));
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(branchId));
    e.dataTransfer.setData("application/json", JSON.stringify({ branchId: String(branchId) }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isOrdering || !draggedId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnd = () => {
    setDraggedId(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    if (!isOrdering || !draggedId) return;
    e.preventDefault();
    if (draggedId === targetId) return;
    const ids = sortedBranches.map((b) => String(b.id));
    const fromIdx = ids.indexOf(draggedId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...ids];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, draggedId);
    setCustomOrderIds(next);
    saveBranchOrder(next);
    setDraggedId(null);
  };

  const handleResetOrder = () => {
    setCustomOrderIds(null);
    localStorage.removeItem(STORAGE_KEY_BRANCH_ORDER);
    setIsOrdering(false);
  };

  if (sortedBranches.length === 0) return null;

  return (
    <div className="card border-0 mx-4 mb-4 mt-0 rounded position-relative">
      <div
        className="d-flex align-items-center gap-2"
        style={{ position: "absolute", top: "0.5rem", left: "0.5rem", zIndex: 1 }}
      >
        <button
          type="button"
          className="btn btn-link text-secondary p-1"
          onClick={() => setIsOrdering((prev) => !prev)}
          title={isOrdering ? "Listo" : "Ordenar sucursales"}
          style={{ minWidth: "32px" }}
        >
          <i className={`fas ${isOrdering ? "fa-check" : "fa-arrows-alt"}`} />
        </button>
        {isOrdering && (
          <button
            type="button"
            className="btn btn-link text-secondary p-1"
            onClick={handleResetOrder}
            title="Restaurar orden por defecto"
          >
            <i className="fas fa-undo" />
          </button>
        )}
      </div>
      <div
        className="p-3 d-flex justify-content-center align-items-center flex-wrap"
        style={{ paddingLeft: "2.25rem" }}
      >
        <div
          className="d-flex flex-wrap align-items-center justify-content-center"
          style={{ gap: "0.5rem" }}
        >
          <button
            type="button"
            className="border-0 text-sm font-medium"
            style={getBranchButtonStyle(selectedBranchId === "all", true)}
            onClick={() => handleBranchClick("all")}
          >
            Todas
          </button>
          {sortedBranches.map((b, index) => {
            const isSelected = selectedBranchId === String(b.id);
            const isDragging = draggedId === String(b.id);
            return (
              <div
                key={b.id}
                draggable={isOrdering}
                onDragStart={(e) => handleDragStart(e, b.id)}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, String(b.id))}
                style={{
                  cursor: isOrdering ? "grab" : "pointer",
                  opacity: isDragging ? 0.5 : 1,
                }}
                className="d-inline-flex align-items-center"
              >
                <button
                  type="button"
                  className="border-0 text-sm font-medium"
                  style={getBranchButtonStyle(isSelected, false, index)}
                  onClick={() => handleBranchClick(b.id)}
                >
                  {b.name}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      {isOrdering && (
        <div className="px-3 pb-2 text-muted small text-center">
          Arrastra las tarjetas para cambiar el orden.
        </div>
      )}
    </div>
  );
};

const ListColaboradores = () => {
  useAppSettings();
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    menuSelected,
    currentPartner,
    page,
    limit,
    param,
    loading,
    fetch,
    query,
    showModal,
    modalChildren,
    modalTitle,
    modalSize,
    show,
    currentUser,
    modalCredentials,
    dataToRemove,
    showDeleteModal,
    showModalImportExcel,
    showModalExportExcel,
    modalAcountant,
    messageModal,
    sortBy,
    defaultSort,
  } = useSelector((state: RootState) => state.colaboradores);
  const { user } = useSelector((state: RootState) => state.auth);

  const fetchColaboradores = async () => {
    dispatch(setLoading(true));
    try {
      const offset = page === 0 ? 0 : page * limit;
      let queryToSend = query;
      if (queryToSend.length === 0) {
        queryToSend = `tab=${menuSelected}`;
      }

      let filter = `?sort=${sortBy}&order=${defaultSort}&offset=${offset}&limit=${limit}&${queryToSend}`;
      if (user.branch) {
        const branches = await fetchDependencies();
        if (!branches) return;
        const branch = branches.find((b) => b.name === user.branch);

        filter = `?sort=${sortBy}&order=${defaultSort}&branch=${branch.id}&offset=${offset}&limit=${limit}&${queryToSend}`;
      }
      const response = await fetchPartners(filter);

      dispatch(setColaboradores(response.results));
      dispatch(setTotal(response.total));
    } catch (error) {
      notification("Error", "Error al cargar los colaboradores", "danger");
    } finally {
      dispatch(setLoading(false));
    }
  };

  React.useEffect(() => {
    fetchColaboradores();
  }, [page, limit, menuSelected, param, fetch, query, sortBy, defaultSort]);

  React.useEffect(() => {
    dispatch(resetState());
  }, [id]);

  const fetchDependencies = async () => {
    try {
      const branchsResponse = await readBranches();
      return branchsResponse;
    } catch (error) {
      notification(
        "Error",
        "Ocurrió un error al tratar de obtener los filtros",
        "danger"
      );
    }
  };

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Colaboradores"
        basePath="/colaboradores"
        title="Colaboradores"
      />
      {can("users.view", user) && (
        <BranchButtonsColaboradores />
      )}
      <div className="card border-0 m-4">
        <div className="tab-content p-3">
          <div className="tab-pane fade show active" id="allTab">
            <FiltrosColaboradores />
            {can("partners.view_imss_salary", user) &&
            !can("partners.edit", user) ? (
              <TableColaboradoresContador />
            ) : (
              <TableColaboradores />
            )}

            <PaginationColaboradores />
          </div>
        </div>
        <ModalContainer
          open={showModal}
          closeFn={() => {
            dispatch(setShowModal(false));
            dispatch(setFetch(!fetch));
          }}
          title={modalTitle}
          size={modalSize}
          children={modalChildren}
        />
        <ModalContainer
          open={show}
          closeFn={() => {
            dispatch(setShow(false));
            dispatch(setFetch(!fetch));
          }}
          title={`Reactivar a ${currentUser?.name}`}
          children={
            <ReactivatePartner
              name={currentUser?.name}
              id={currentUser?.id}
              closeFn={() => {
                dispatch(setShow(false));
                dispatch(setFetch(!fetch));
              }}
            />
          }
        />
        <ModalContainer
          open={modalCredentials}
          closeFn={() => {
            dispatch(setModalCredentials(false));
            dispatch(setFetch(!fetch));
          }}
          title={`Credenciales de ${currentUser?.name}`}
          children={
            <GetCredentials
              colaborador={currentUser}
              name={currentUser?.name}
              id={currentUser?.id}
              closeFn={() => {
                dispatch(setModalCredentials(false));
                dispatch(setFetch(!fetch));
              }}
            />
          }
        />
        <ModalContainer
          open={modalAcountant}
          closeFn={() => dispatch(setModalAcountant(false))}
          title={`Editar salario`}
          children={
            <EditSalary
              colaboradorId={currentUser}
              closeFn={() => dispatch(setModalAcountant(false))}
              refetch={() => {}}
            />
          }
        />
        <ModalContainer
          open={messageModal}
          closeFn={() => {
            dispatch(setMessageModal(false));
            dispatch(setFetch(!fetch));
          }}
          title={`Mensaje para ${toCamelCase(currentUser?.name)}`}
          children={
            <EditColaboradorMessage
              colaborador={currentUser}
              closeFn={() => {
                dispatch(setMessageModal(false));
                dispatch(setFetch(!fetch));
              }}
            />
          }
        />
        <ModalContainer
          open={showModalImportExcel}
          closeFn={() => dispatch(setShowModalImportExcel(false))}
          title="Importar colaboradores"
          children={<ImportPartners />}
        />
        <ModalContainer
          open={showModalExportExcel}
          closeFn={() => dispatch(setShowModalExportExcel(false))}
          title="Exportar colaboradores"
          size="xl"
          children={
            <ExportColaboradoresModal
              query={query}
              sortBy={sortBy}
              defaultSort={defaultSort}
              menuSelected={menuSelected}
              closeFn={() => dispatch(setShowModalExportExcel(false))}
            />
          }
        />
        <ModalContainer
          open={showDeleteModal}
          closeFn={() => {
            dispatch(setModalDelete(false));
            dispatch(
              setDataToRemove({
                id: -1,
                name: "",
              })
            );
            dispatch(setFetch(!fetch));
          }}
          title={`Dar de baja a ${toCamelCase(dataToRemove?.name)}`}
          children={
            <DeletePartner
              name={dataToRemove?.name}
              programed={dataToRemove?.programed}
              id={dataToRemove?.id}
              closeFn={() => {
                dispatch(setModalDelete(false));
                dispatch(
                  setDataToRemove({
                    id: -1,
                    name: "",
                  })
                );
                dispatch(setFetch(!fetch));
                dispatch(setPage(0));
              }}
            />
          }
        />
      </div>
    </Layout>
  );
};

export default ListColaboradores;
